import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';

import { projectsApi } from '@/lib/apis';

import { downloadBlob, pLimit } from '@nablax/shared/utils';
import { ExportableJSONFile } from '../../types/exports';
import { getProjectFileExts } from '../../utils/project-files';
import { loadCloudProject } from '../project/project.thunks';

export const exportProjectFiles = createAsyncThunk<
  void,
  { projectId: string; file: ExportableJSONFile; exportJSON?: boolean }
>('export/download', async ({ file, projectId, exportJSON = false }) => {
  const exts = exportJSON
    ? ['json']
    : getProjectFileExts(file).filter((e) => e !== 'json');
  const fetchExt = async (ext: string, fullName: string) => {
    let result: AxiosResponse<any>;

    if (ext === 'json') {
      result = await projectsApi.projectsFilesGet(projectId, fullName, {
        responseType: 'blob',
      });
    } else {
      result = await projectsApi.projectsExportsGet(projectId, fullName, {
        responseType: 'blob',
      });
    }

    downloadBlob(
      `${file.json.content.displayProperties.name}.${ext}`,
      result.data,
    );
  };

  const promises = exts.map((ext) => {
    const fullName = file[ext as keyof typeof file]!.fullName;
    return () => fetchExt(ext, fullName);
  });

  await pLimit<void>(promises, 2);
});

export const deleteExportFiles = createAsyncThunk<
  void,
  { projectId: string; files: ExportableJSONFile[] }
>('export/delete', async ({ projectId, files }, { dispatch }) => {
  const deleteExts = async (fullNames: string[]) => {
    for (let i = 0; i < fullNames.length; i++) {
      const fullName = fullNames[i];
      await projectsApi.projectsFilesDelete(projectId, fullName);
    }
  };

  const promises = files.map((file) => {
    const exts = getProjectFileExts(file);
    const fullNames = exts.map(
      (ext) => file[ext as keyof typeof file]!.fullName,
    );
    return () => deleteExts(fullNames);
  });

  await pLimit<void>(promises, 2);
  // tracking issue: https://gitlab.com/nablawave_srl/nablax/plugins/projects/-/issues/9
  await projectsApi.projectsFilesDelete(projectId, '_project.zip');
  dispatch(loadCloudProject({ projectId: projectId, type: 'cloud' }));
});
